/**
 * Generated by orval 🍺
 * Do not edit manually.
 * TrainLoop Platform V2 API
 * Model evaluation workbench API
 * OpenAPI spec version: 0.0.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  HTTPValidationError,
  ReviewChatRequest,
  ReviewQueueMetadataResponse,
  ReviewQueueModelsResponse,
  ReviewQueuePrStatusResponse,
  ReviewQueueResponse,
} from "../model";

import { orvalFetch } from "../../client.ts";
import type { ErrorType } from "../../client.ts";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === "queryKey") continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export type getReviewQueueApiReviewQueueGetResponse200 = {
  data: ReviewQueueResponse;
  status: 200;
};

export type getReviewQueueApiReviewQueueGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getReviewQueueApiReviewQueueGetResponseSuccess =
  getReviewQueueApiReviewQueueGetResponse200 & {
    headers: Headers;
  };
export type getReviewQueueApiReviewQueueGetResponseError =
  getReviewQueueApiReviewQueueGetResponse422 & {
    headers: Headers;
  };

export type getReviewQueueApiReviewQueueGetResponse =
  | getReviewQueueApiReviewQueueGetResponseSuccess
  | getReviewQueueApiReviewQueueGetResponseError;

export const getGetReviewQueueApiReviewQueueGetUrl = () => {
  return `/api/review/queue`;
};

/**
 * The team's review queue: labels + virtual eval flags with locations.
 *
 * Strictly read-only: failing eval cases without a label surface as
 * virtual items (`label_id` null); the label row is created only when
 * the reviewer saves via POST /api/labels. GETs must not write — staff
 * impersonation and restricted demo teams pass read-only guards. The
 * audit log's own write is a background one (TRA-1626), so that holds.
 *
 * Deliberately does NOT resolve each trace's model: that costs one R2
 * body GET per row and made first paint crawl on long queues. The model
 * filter hydrates from GET /queue/models instead.
 * @summary Get Review Queue
 */
export const getReviewQueueApiReviewQueueGet = async (
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getReviewQueueApiReviewQueueGetResponse> => {
  return orvalFetch<getReviewQueueApiReviewQueueGetResponse>(
    getGetReviewQueueApiReviewQueueGetUrl(),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetReviewQueueApiReviewQueueGetQueryKey = () => {
  return [`/api/review/queue`] as const;
};

export const getGetReviewQueueApiReviewQueueGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getReviewQueueApiReviewQueueGet>>,
  TError = ErrorType<HTTPValidationError>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getReviewQueueApiReviewQueueGet>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof orvalFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetReviewQueueApiReviewQueueGetQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getReviewQueueApiReviewQueueGet>>
  > = ({ signal }) =>
    getReviewQueueApiReviewQueueGet({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getReviewQueueApiReviewQueueGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetReviewQueueApiReviewQueueGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getReviewQueueApiReviewQueueGet>>
>;
export type GetReviewQueueApiReviewQueueGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetReviewQueueApiReviewQueueGet<
  TData = Awaited<ReturnType<typeof getReviewQueueApiReviewQueueGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getReviewQueueApiReviewQueueGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getReviewQueueApiReviewQueueGet>>,
          TError,
          Awaited<ReturnType<typeof getReviewQueueApiReviewQueueGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetReviewQueueApiReviewQueueGet<
  TData = Awaited<ReturnType<typeof getReviewQueueApiReviewQueueGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getReviewQueueApiReviewQueueGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getReviewQueueApiReviewQueueGet>>,
          TError,
          Awaited<ReturnType<typeof getReviewQueueApiReviewQueueGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetReviewQueueApiReviewQueueGet<
  TData = Awaited<ReturnType<typeof getReviewQueueApiReviewQueueGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getReviewQueueApiReviewQueueGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Review Queue
 */

export function useGetReviewQueueApiReviewQueueGet<
  TData = Awaited<ReturnType<typeof getReviewQueueApiReviewQueueGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getReviewQueueApiReviewQueueGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetReviewQueueApiReviewQueueGetQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getReviewQueueModelsApiReviewQueueModelsGetResponse200 = {
  data: ReviewQueueModelsResponse;
  status: 200;
};

export type getReviewQueueModelsApiReviewQueueModelsGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getReviewQueueModelsApiReviewQueueModelsGetResponseSuccess =
  getReviewQueueModelsApiReviewQueueModelsGetResponse200 & {
    headers: Headers;
  };
export type getReviewQueueModelsApiReviewQueueModelsGetResponseError =
  getReviewQueueModelsApiReviewQueueModelsGetResponse422 & {
    headers: Headers;
  };

export type getReviewQueueModelsApiReviewQueueModelsGetResponse =
  | getReviewQueueModelsApiReviewQueueModelsGetResponseSuccess
  | getReviewQueueModelsApiReviewQueueModelsGetResponseError;

export const getGetReviewQueueModelsApiReviewQueueModelsGetUrl = () => {
  return `/api/review/queue/models`;
};

/**
 * Trace key → model slug for every row in the team's queue (TRA-907).
 *
 * The slow half of the old queue response, split out so the sidebar can
 * paint before these trace-body reads finish. Recomputes the queue's key
 * set server-side rather than accepting client-supplied keys — no
 * cross-tenant key can ever be probed through this endpoint. The keys
 * name the team's runs, so the read is recorded in the audit log
 * (TRA-1626) like the queue itself.
 * @summary Get Review Queue Models
 */
export const getReviewQueueModelsApiReviewQueueModelsGet = async (
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getReviewQueueModelsApiReviewQueueModelsGetResponse> => {
  return orvalFetch<getReviewQueueModelsApiReviewQueueModelsGetResponse>(
    getGetReviewQueueModelsApiReviewQueueModelsGetUrl(),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetReviewQueueModelsApiReviewQueueModelsGetQueryKey = () => {
  return [`/api/review/queue/models`] as const;
};

export const getGetReviewQueueModelsApiReviewQueueModelsGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getReviewQueueModelsApiReviewQueueModelsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getReviewQueueModelsApiReviewQueueModelsGet>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof orvalFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetReviewQueueModelsApiReviewQueueModelsGetQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getReviewQueueModelsApiReviewQueueModelsGet>>
  > = ({ signal }) =>
    getReviewQueueModelsApiReviewQueueModelsGet({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getReviewQueueModelsApiReviewQueueModelsGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetReviewQueueModelsApiReviewQueueModelsGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getReviewQueueModelsApiReviewQueueModelsGet>>
  >;
export type GetReviewQueueModelsApiReviewQueueModelsGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetReviewQueueModelsApiReviewQueueModelsGet<
  TData = Awaited<
    ReturnType<typeof getReviewQueueModelsApiReviewQueueModelsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getReviewQueueModelsApiReviewQueueModelsGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getReviewQueueModelsApiReviewQueueModelsGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getReviewQueueModelsApiReviewQueueModelsGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetReviewQueueModelsApiReviewQueueModelsGet<
  TData = Awaited<
    ReturnType<typeof getReviewQueueModelsApiReviewQueueModelsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getReviewQueueModelsApiReviewQueueModelsGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getReviewQueueModelsApiReviewQueueModelsGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getReviewQueueModelsApiReviewQueueModelsGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetReviewQueueModelsApiReviewQueueModelsGet<
  TData = Awaited<
    ReturnType<typeof getReviewQueueModelsApiReviewQueueModelsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getReviewQueueModelsApiReviewQueueModelsGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Review Queue Models
 */

export function useGetReviewQueueModelsApiReviewQueueModelsGet<
  TData = Awaited<
    ReturnType<typeof getReviewQueueModelsApiReviewQueueModelsGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getReviewQueueModelsApiReviewQueueModelsGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetReviewQueueModelsApiReviewQueueModelsGetQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getReviewQueueMetadataApiReviewQueueMetadataGetResponse200 = {
  data: ReviewQueueMetadataResponse;
  status: 200;
};

export type getReviewQueueMetadataApiReviewQueueMetadataGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getReviewQueueMetadataApiReviewQueueMetadataGetResponseSuccess =
  getReviewQueueMetadataApiReviewQueueMetadataGetResponse200 & {
    headers: Headers;
  };
export type getReviewQueueMetadataApiReviewQueueMetadataGetResponseError =
  getReviewQueueMetadataApiReviewQueueMetadataGetResponse422 & {
    headers: Headers;
  };

export type getReviewQueueMetadataApiReviewQueueMetadataGetResponse =
  | getReviewQueueMetadataApiReviewQueueMetadataGetResponseSuccess
  | getReviewQueueMetadataApiReviewQueueMetadataGetResponseError;

export const getGetReviewQueueMetadataApiReviewQueueMetadataGetUrl = () => {
  return `/api/review/queue/metadata`;
};

/**
 * Trace key → client metadata for the team's current review queue.
 *
 * Metadata is read from each exact immutable trace body with a bounded,
 * cached fan-out. The endpoint derives the team's keys server-side and never
 * accepts caller-owned trace keys. Absent headers are explicit nulls rather
 * than silently dropping queue rows; a trace whose body could not be read
 * is returned with `readable` false and every field null. The metadata
 * is the customer's (tenant, session, who started the run), so the read
 * is recorded in the audit log (TRA-1626).
 * @summary Get Review Queue Metadata
 */
export const getReviewQueueMetadataApiReviewQueueMetadataGet = async (
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getReviewQueueMetadataApiReviewQueueMetadataGetResponse> => {
  return orvalFetch<getReviewQueueMetadataApiReviewQueueMetadataGetResponse>(
    getGetReviewQueueMetadataApiReviewQueueMetadataGetUrl(),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetReviewQueueMetadataApiReviewQueueMetadataGetQueryKey =
  () => {
    return [`/api/review/queue/metadata`] as const;
  };

export const getGetReviewQueueMetadataApiReviewQueueMetadataGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getReviewQueueMetadataApiReviewQueueMetadataGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<
        ReturnType<typeof getReviewQueueMetadataApiReviewQueueMetadataGet>
      >,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof orvalFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetReviewQueueMetadataApiReviewQueueMetadataGetQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getReviewQueueMetadataApiReviewQueueMetadataGet>>
  > = ({ signal }) =>
    getReviewQueueMetadataApiReviewQueueMetadataGet({
      signal,
      ...requestOptions,
    });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getReviewQueueMetadataApiReviewQueueMetadataGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetReviewQueueMetadataApiReviewQueueMetadataGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getReviewQueueMetadataApiReviewQueueMetadataGet>>
  >;
export type GetReviewQueueMetadataApiReviewQueueMetadataGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetReviewQueueMetadataApiReviewQueueMetadataGet<
  TData = Awaited<
    ReturnType<typeof getReviewQueueMetadataApiReviewQueueMetadataGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getReviewQueueMetadataApiReviewQueueMetadataGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getReviewQueueMetadataApiReviewQueueMetadataGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getReviewQueueMetadataApiReviewQueueMetadataGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetReviewQueueMetadataApiReviewQueueMetadataGet<
  TData = Awaited<
    ReturnType<typeof getReviewQueueMetadataApiReviewQueueMetadataGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getReviewQueueMetadataApiReviewQueueMetadataGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getReviewQueueMetadataApiReviewQueueMetadataGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getReviewQueueMetadataApiReviewQueueMetadataGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetReviewQueueMetadataApiReviewQueueMetadataGet<
  TData = Awaited<
    ReturnType<typeof getReviewQueueMetadataApiReviewQueueMetadataGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getReviewQueueMetadataApiReviewQueueMetadataGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Review Queue Metadata
 */

export function useGetReviewQueueMetadataApiReviewQueueMetadataGet<
  TData = Awaited<
    ReturnType<typeof getReviewQueueMetadataApiReviewQueueMetadataGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getReviewQueueMetadataApiReviewQueueMetadataGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetReviewQueueMetadataApiReviewQueueMetadataGetQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getReviewQueuePrStatusApiReviewQueuePrStatusGetResponse200 = {
  data: ReviewQueuePrStatusResponse;
  status: 200;
};

export type getReviewQueuePrStatusApiReviewQueuePrStatusGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getReviewQueuePrStatusApiReviewQueuePrStatusGetResponseSuccess =
  getReviewQueuePrStatusApiReviewQueuePrStatusGetResponse200 & {
    headers: Headers;
  };
export type getReviewQueuePrStatusApiReviewQueuePrStatusGetResponseError =
  getReviewQueuePrStatusApiReviewQueuePrStatusGetResponse422 & {
    headers: Headers;
  };

export type getReviewQueuePrStatusApiReviewQueuePrStatusGetResponse =
  | getReviewQueuePrStatusApiReviewQueuePrStatusGetResponseSuccess
  | getReviewQueuePrStatusApiReviewQueuePrStatusGetResponseError;

export const getGetReviewQueuePrStatusApiReviewQueuePrStatusGetUrl = () => {
  return `/api/review/queue/pr-status`;
};

/**
 * PR url → GitHub state for the team's labels that carry one.
 *
 * Cheap by design: reads pr_urls straight from the team's label rows (no
 * R2 key resolution) and asks GitHub only for URLs not already settled in
 * the process cache. URLs come from the DB, never the caller, so no
 * foreign PR can be probed through this endpoint.
 * @summary Get Review Queue Pr Status
 */
export const getReviewQueuePrStatusApiReviewQueuePrStatusGet = async (
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getReviewQueuePrStatusApiReviewQueuePrStatusGetResponse> => {
  return orvalFetch<getReviewQueuePrStatusApiReviewQueuePrStatusGetResponse>(
    getGetReviewQueuePrStatusApiReviewQueuePrStatusGetUrl(),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetReviewQueuePrStatusApiReviewQueuePrStatusGetQueryKey =
  () => {
    return [`/api/review/queue/pr-status`] as const;
  };

export const getGetReviewQueuePrStatusApiReviewQueuePrStatusGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getReviewQueuePrStatusApiReviewQueuePrStatusGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<
        ReturnType<typeof getReviewQueuePrStatusApiReviewQueuePrStatusGet>
      >,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof orvalFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetReviewQueuePrStatusApiReviewQueuePrStatusGetQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getReviewQueuePrStatusApiReviewQueuePrStatusGet>>
  > = ({ signal }) =>
    getReviewQueuePrStatusApiReviewQueuePrStatusGet({
      signal,
      ...requestOptions,
    });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getReviewQueuePrStatusApiReviewQueuePrStatusGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetReviewQueuePrStatusApiReviewQueuePrStatusGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getReviewQueuePrStatusApiReviewQueuePrStatusGet>>
  >;
export type GetReviewQueuePrStatusApiReviewQueuePrStatusGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetReviewQueuePrStatusApiReviewQueuePrStatusGet<
  TData = Awaited<
    ReturnType<typeof getReviewQueuePrStatusApiReviewQueuePrStatusGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getReviewQueuePrStatusApiReviewQueuePrStatusGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getReviewQueuePrStatusApiReviewQueuePrStatusGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getReviewQueuePrStatusApiReviewQueuePrStatusGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetReviewQueuePrStatusApiReviewQueuePrStatusGet<
  TData = Awaited<
    ReturnType<typeof getReviewQueuePrStatusApiReviewQueuePrStatusGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getReviewQueuePrStatusApiReviewQueuePrStatusGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getReviewQueuePrStatusApiReviewQueuePrStatusGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getReviewQueuePrStatusApiReviewQueuePrStatusGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetReviewQueuePrStatusApiReviewQueuePrStatusGet<
  TData = Awaited<
    ReturnType<typeof getReviewQueuePrStatusApiReviewQueuePrStatusGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getReviewQueuePrStatusApiReviewQueuePrStatusGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Review Queue Pr Status
 */

export function useGetReviewQueuePrStatusApiReviewQueuePrStatusGet<
  TData = Awaited<
    ReturnType<typeof getReviewQueuePrStatusApiReviewQueuePrStatusGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getReviewQueuePrStatusApiReviewQueuePrStatusGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetReviewQueuePrStatusApiReviewQueuePrStatusGetQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type reviewChatStreamApiReviewChatPostResponse200 = {
  data: unknown;
  status: 200;
};

export type reviewChatStreamApiReviewChatPostResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type reviewChatStreamApiReviewChatPostResponseSuccess =
  reviewChatStreamApiReviewChatPostResponse200 & {
    headers: Headers;
  };
export type reviewChatStreamApiReviewChatPostResponseError =
  reviewChatStreamApiReviewChatPostResponse422 & {
    headers: Headers;
  };

export type reviewChatStreamApiReviewChatPostResponse =
  | reviewChatStreamApiReviewChatPostResponseSuccess
  | reviewChatStreamApiReviewChatPostResponseError;

export const getReviewChatStreamApiReviewChatPostUrl = () => {
  return `/api/review/chat`;
};

/**
 * Stream an answer about one queued trajectory, grounded in its traces.
 *
 * The trajectory is rebuilt server-side from `trace_key` and rendered under
 * a character budget; the client supplies only its own question history. No
 * DB writes, no persistence — the thread lives in the reviewer's tab. The
 * one write is the audit log's (TRA-1626): the chat reads the whole run,
 * so it is recorded like opening the run.
 * @summary Review Chat Stream
 */
export const reviewChatStreamApiReviewChatPost = async (
  reviewChatRequest: ReviewChatRequest,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<reviewChatStreamApiReviewChatPostResponse> => {
  const getHeaders = (
    h?: NonNullable<RequestInit["headers"]>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(
          h as Iterable<Iterable<string>>,
          (entry) => Array.from(entry) as [string, string],
        ),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<
      string | readonly string[] | undefined
    >(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return orvalFetch<reviewChatStreamApiReviewChatPostResponse>(
    getReviewChatStreamApiReviewChatPostUrl(),
    {
      ...options,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getHeaders(options?.headers),
      },
      body: JSON.stringify(reviewChatRequest),
    },
  );
};

export const getReviewChatStreamApiReviewChatPostMutationKey = () =>
  ["reviewChatStreamApiReviewChatPost"] as const;

export const getReviewChatStreamApiReviewChatPostMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof reviewChatStreamApiReviewChatPost>>,
    TError,
    ReviewChatStreamApiReviewChatPostMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof orvalFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof reviewChatStreamApiReviewChatPost>>,
  TError,
  ReviewChatStreamApiReviewChatPostMutationVariables,
  TContext
> => {
  const mutationKey = getReviewChatStreamApiReviewChatPostMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof reviewChatStreamApiReviewChatPost>>,
    ReviewChatStreamApiReviewChatPostMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return reviewChatStreamApiReviewChatPost(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ReviewChatStreamApiReviewChatPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof reviewChatStreamApiReviewChatPost>>
>;
export type ReviewChatStreamApiReviewChatPostMutationBody = ReviewChatRequest;
export type ReviewChatStreamApiReviewChatPostMutationError =
  ErrorType<HTTPValidationError>;
export type ReviewChatStreamApiReviewChatPostMutationVariables = {
  data: ReviewChatRequest;
};

/**
 * @summary Review Chat Stream
 */
export const useReviewChatStreamApiReviewChatPost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof reviewChatStreamApiReviewChatPost>>,
      TError,
      ReviewChatStreamApiReviewChatPostMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof reviewChatStreamApiReviewChatPost>>,
  TError,
  ReviewChatStreamApiReviewChatPostMutationVariables,
  TContext
> => {
  return useMutation(
    getReviewChatStreamApiReviewChatPostMutationOptions(options),
    queryClient,
  );
};
